/* eslint-disable react/jsx-no-bind */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-use-before-define */

import {
  type MRT_TableOptions,
  type MRT_PaginationState,
  type MRT_SortingState,
  Button,
  Box,
  H2,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  TextInput,
  ListDriveInfo,
  MRT_Row,
  RecordingProcessStatus,
  RowActionButton,
  Icon,
  OrganizationRecording,
  ColumnFilterPopover,
  FilterDropDown,
  mapCategoryForDisplay,
  useDisclosure,
  BaseRecordingTableColumn,
  baseRecordingColumnMapping,
  EditRecording,
  AccessLevel,
  ModalContainer,
  RecordingStatus,
} from '@ouster/webviz-react-components';

import {
  createTheme,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { uniq } from 'lodash';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MdRefresh, MdClear, MdSearch, MdMoreVert } from 'react-icons/md';
import { TbEyeStar } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import RecordingAccessConfig from '../../components/recordingTable/RecordingAccessConfig';
import RecordingDownloadPopover from '../../components/recordingTable/RecordingDownloadPopover';
import ReUploadExtrinsic from '../../components/recordingTable/ReUploadExtrinsic';
import { useColumnVisibility } from '../../components/recordingTable/useColumnVisibility';
import useFilters from '../../components/recordingTable/useFilters';
import {
  usePrivateListQuery,
  getDefaultPageSize,
  setDefaultPageSize,
} from '../../components/recordingTable/useRecordingListQuery';
import {
  useSearchIndex,
  SearchIndexRecord,
} from '../../components/recordingTable/useSearchIndex';
import useChakraHook from '../../hooks/useChakraHook';
import { useStoreState, useStoreActions } from '../../reducers';
import { RecordingPublicityAccessConfig } from '../../types';
import { DRIVES_PER_PAGE, FILTER_GROUPS } from '../../utils/constants';
import { convertFilterArrayToObject } from '../../utils/driveUtil';
import {
  createPaginationConfig,
  hasUserSelectionOnTable,
} from '../../utils/tableUtils';

const shortenFilterForPopover = (filters: Record<string, string[]>) => {
  const copy = { ...filters };
  if (copy.data_category) {
    const mappedCategory = copy.data_category.map((category) =>
      mapCategoryForDisplay(category)
    );

    copy.data_category = uniq(mappedCategory);
  }

  return copy;
};

const saveSearchToLocalStorage = (search: string, orgId: number) => {
  const searchKey = `search_${orgId}`;
  localStorage.setItem(searchKey, search);
};

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

type RowActionsProps = {
  row: MRT_Row<ListDriveInfo>;
  copyCloudVizLink: (value: ListDriveInfo) => void;
  copyCloudPublicLink: (value: ListDriveInfo) => void;
  handleDelete: (value: ListDriveInfo) => void;
  reprocessRecording: (value: ListDriveInfo) => void;
  reUploadExtrinsic: (value: ListDriveInfo) => void;
  configRecordingPublicity: (value: ListDriveInfo) => void;
  updateRecordingInfo: (value: ListDriveInfo) => void;
  openRecordingDownloadPopover: (
    row: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void;
};

const RowActions = ({
  row,
  copyCloudVizLink,
  copyCloudPublicLink,
  handleDelete,
  reprocessRecording,
  reUploadExtrinsic,
  configRecordingPublicity,
  openRecordingDownloadPopover,
  updateRecordingInfo,
}: RowActionsProps) => {
  const { curUserOrg, appUser, isOusterUser } = useStoreState(
    (storeState) => storeState.user
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const createClickHandler =
    (handler: (r: ListDriveInfo) => void) =>
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      handler(row.original);
      handleClose();
    };

  const isOwner = appUser?.id === row.original.uploaded_by_id;
  const isAdmin = curUserOrg?.role === 'ADMIN';
  const isFailed = row.original.status === RecordingProcessStatus.Failed;

  const conditionalActions = [
    !isFailed && {
      title: 'Edit File Name',
      icon: <Icon icon="edit" />,
      handler: createClickHandler(updateRecordingInfo),
    },
    isOusterUser &&
      isAdmin && {
        title: 'Configure Sample Access',
        icon: <TbEyeStar size={24} />,
        handler: createClickHandler(configRecordingPublicity),
      },
    isOusterUser &&
      (isAdmin || isOwner) && {
        title: 'Reprocess recording',
        icon: <Icon icon="refresh" />,
        handler: createClickHandler(reprocessRecording),
      },
    (isAdmin || isOwner) &&
      row.original.sensor_info.length > 1 && {
        title: 'Re-upload extrinsic',
        icon: <Icon icon="upload" />,
        handler: createClickHandler(reUploadExtrinsic),
      },
    (isOwner || isAdmin) && {
      title: 'Delete recording',
      icon: <Icon icon="delete" />,
      handler: createClickHandler(handleDelete),
    },
    row.original.public_url && {
      title: 'Copy public link',
      icon: <Icon icon="public" />,
      handler: createClickHandler(copyCloudPublicLink),
    },
  ].filter(Boolean);

  return (
    <Box
      flexWrap="nowrap"
      display="flex"
      alignItems="center"
      data-testid="private-recordings-row-action-buttons"
    >
      <RowActionButton
        title="Copy the visualization link"
        icon={<Icon icon="share" />}
        row={row.original}
        onClickHandler={copyCloudVizLink}
      />
      <RowActionButton
        title="Download recording"
        icon={<Icon icon="download" />}
        row={row.original}
        onClickHandler={openRecordingDownloadPopover}
      />

      {conditionalActions.length > 0 && (
        <ThemeProvider theme={darkTheme}>
          <IconButton
            aria-label="More actions"
            id="more-actions-button"
            aria-controls={open ? 'more-actions-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
          >
            <MdMoreVert color="white" />
          </IconButton>
          <Menu
            id="more-actions-menu"
            anchorEl={anchorEl}
            open={open}
            disableAutoFocusItem
            onClose={(event: Event) => {
              event.stopPropagation();
              handleClose();
            }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: 'var(--ouster-colors-background-surface)',
                  backgroundImage: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  outline: '1px solid var(--ouster-colors-border-divider)',
                  boxShadow: 'none',
                  mt: '4px',
                },
              },
            }}
            MenuListProps={{
              'aria-labelledby': 'more-actions-button',
              sx: {
                py: '4px',
              },
            }}
          >
            {conditionalActions.map(
              (action) =>
                action && (
                  <MenuItem
                    key={action.title}
                    onClick={action.handler}
                    sx={{
                      color: 'white',
                      fontSize: '14px',
                      py: '0.75rem',
                      px: '1.25rem',
                      '&:hover, &:focus': {
                        backgroundColor: 'var(--color-blast-off-blue)',
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      {action.icon}
                      {action.title}
                    </Box>
                  </MenuItem>
                )
            )}
          </Menu>
        </ThemeProvider>
      )}
    </Box>
  );
};
const DEFAULT_COLUMN_VISIBILITY = {
  start_time: false,
  sdk_fw_branch: false,
  sdk_fw_build_rev: false,
  sdk_fw_image_rev: false,
};

const TIMESTAMP_SORT_KEYS = new Set([
  'processed_at',
  'start_time',
  'stop_time',
]);

const toEpochMs = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Date.parse(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const getDurationMs = (drive: ListDriveInfo): number | null => {
  const row = drive as Record<string, unknown>;
  const startMs = toEpochMs(row.start_time);
  const stopMs = toEpochMs(row.stop_time);
  if (startMs === null || stopMs === null) return null;

  const durationMs = stopMs - startMs;
  return durationMs > 0 ? durationMs : null;
};

const getDurationSortValue = (drive: ListDriveInfo): number | null => {
  const row = drive as Record<string, unknown>;
  const durationValueRaw = row.durationValue;

  if (
    typeof durationValueRaw === 'number' &&
    Number.isFinite(durationValueRaw)
  ) {
    // durationValue is in seconds in ListDriveInfo; convert to ms.
    const durationMs = durationValueRaw * 1000;
    return durationMs > 0 ? durationMs : null;
  }

  return getDurationMs(drive);
};

const normalizeSortValue = (
  value: unknown,
  accessorKey: string
): string | number => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) return value.getTime();

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return '';

    // Uploaded/recorded columns can be formatted date strings in cache mode.
    // Parse to epoch ms so sorting is chronological instead of lexicographic.
    if (TIMESTAMP_SORT_KEYS.has(accessorKey)) {
      const parsed = Date.parse(trimmed);
      if (!Number.isNaN(parsed)) return parsed;
    }

    return trimmed.toLowerCase();
  }

  return String(value).toLowerCase();
};

const compareDriveValues = (
  a: ListDriveInfo,
  b: ListDriveInfo,
  key: string,
  desc: boolean
): number => {
  if (key === 'duration') {
    const leftParsed = getDurationSortValue(a);
    const rightParsed = getDurationSortValue(b);

    // Missing / zero durations are always pushed to the end,
    // regardless of ascending or descending order.
    const leftHasDuration = leftParsed !== null && leftParsed > 0;
    const rightHasDuration = rightParsed !== null && rightParsed > 0;

    if (leftHasDuration && !rightHasDuration) return -1;
    if (!leftHasDuration && rightHasDuration) return 1;
    if (!leftHasDuration && !rightHasDuration) return 0;

    const numericResult = (leftParsed as number) - (rightParsed as number);
    return desc ? -numericResult : numericResult;
  }

  const lhs = normalizeSortValue((a as Record<string, unknown>)[key], key);
  const rhs = normalizeSortValue((b as Record<string, unknown>)[key], key);

  let result = 0;
  if (typeof lhs === 'number' && typeof rhs === 'number') {
    result = lhs - rhs;
  } else {
    result = String(lhs).localeCompare(String(rhs));
  }

  return desc ? -result : result;
};

/**
 *
 */
export default function PrivateRecordings() {
  const { curUserOrg, isOusterUser } = useStoreState(
    (storeState) => storeState.user
  );

  const {
    deleteDrive,
    reprocessDrive,
    setSourceFileInfo,
    setOutputFileInfo,
    updateRecordingTitle,
    loadNewContentStatus,
  } = useStoreActions((actions) => actions.drive);

  const { openConfirmDialog } = useStoreActions(
    (storeState) => storeState.confirmDialog
  );
  // get filterable options
  const { loadOrgFilterOptions } = useStoreActions(
    (actions) => actions.driveList
  );
  const { orgFilterOptions } = useStoreState((storeState) => ({
    ...storeState.driveList,
  }));
  const filterOptions = convertFilterArrayToObject(orgFilterOptions);

  const [pagination, setPagination] = useState<MRT_PaginationState>(() => ({
    pageIndex: 0,
    pageSize: getDefaultPageSize(DRIVES_PER_PAGE),
  }));

  // Persist pageSize changes
  const handlePaginationChange: MRT_TableOptions<ListDriveInfo>['onPaginationChange'] =
    (updater) => {
      setPagination((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: MRT_PaginationState) => MRT_PaginationState)(prev)
            : updater;
        if (next.pageSize !== prev.pageSize) {
          setDefaultPageSize(next.pageSize);
        }
        if (next.pageIndex !== prev.pageIndex) {
          setPageIndex(next.pageIndex);
        }
        return next;
      });
    };
  const [isReprocessing, setIsReprocessing] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const tableContainer = tableContainerRef.current?.querySelector(
      '.MuiTableContainer-root'
    ) as HTMLElement;
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  }, [pagination.pageIndex]);

  /* 
    Control for extrinsic re-upload modal
  */
  const {
    isOpen: isExtrinsicOpen,
    onOpen: onExtrinsicOpen,
    onClose: onExtrinsicClose,
  } = useDisclosure();
  const extrinsicRecordingRef = useRef<ListDriveInfo | undefined>(undefined);

  const {
    isOpen: isRecordingInfoModalOpen,
    onOpen: onRecordingInfoModalOpen,
    onClose: onRecordingInfoModalClose,
  } = useDisclosure();
  const recordingInfoRef = useRef<ListDriveInfo | undefined>(undefined);

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    filters,
    orderBy,
    sortOrder,
    pageIndex,
    isHydrated,
    setOrderBy,
    setPageIndex,
    setGroupFilters,
  } = useFilters('processed_at', 'desc');
  const { showToast } = useChakraHook();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const previousDebouncedSearchRef = useRef<string | null>(null);
  const previousOrgIdRef = useRef<number | null>(null);
  const [orgSwitchPending, setOrgSwitchPending] = useState(false);
  const reconcileTimerRef = useRef<number | null>(null);

  // Client-side search index (MiniSearch in Web Worker)
  const searchIndexState: ReturnType<typeof useSearchIndex> = useSearchIndex(
    curUserOrg,
    debouncedSearchTerm,
    filters
  );

  const {
    isIndexReady,
    indexOrgId,
    hasFreshOrgCache,
    isIndexRefreshInFlight,
    searchResults,
    allRecords,
    refreshIndex,
    updateIndexedRecordingTitle,
    updateIndexedDrive,
    removeIndexedRecording,
    addIndexedRecording,
  } = searchIndexState;
  const isOrgChanging = Boolean(sessionStorage.getItem('orgChanged'));
  const isLocalDataMode =
    !isOrgChanging &&
    !!curUserOrg?.id &&
    isIndexReady &&
    indexOrgId === curUserOrg.id;
  const shouldPreferLocalSource =
    isLocalDataMode || hasFreshOrgCache || isIndexRefreshInFlight;
  const [searchRestoredOrgId, setSearchRestoredOrgId] = useState<number | null>(
    null
  );
  const isSearchRestored =
    !!curUserOrg?.id && searchRestoredOrgId === curUserOrg.id;
  const isPaginationSynced = pagination.pageIndex === pageIndex;
  const isQueryEnabled =
    isHydrated &&
    isSearchRestored &&
    isPaginationSynced &&
    !isOrgChanging &&
    !shouldPreferLocalSource;
  const [sorting, setSorting] = useState<MRT_SortingState>(() => [
    {
      id: orderBy || 'processed_at',
      desc: sortOrder ? sortOrder === 'desc' : true,
    },
  ]);

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm === '' || searchTerm.length >= 2) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 100); // 100ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Keep keyword search and URL pagination in sync.
  // When the debounced keyword changes (including clearing it), force page 1
  // in both local table state and URL state to avoid fetching stale page data.
  useEffect(() => {
    const previousDebouncedSearch = previousDebouncedSearchRef.current ?? '';
    const hasSearchChanged = previousDebouncedSearch !== debouncedSearchTerm;
    const isSearchStarted = debouncedSearchTerm.trim() !== '';
    const isSearchCleared =
      previousDebouncedSearch.trim() !== '' &&
      debouncedSearchTerm.trim() === '';

    if (hasSearchChanged && (isSearchStarted || isSearchCleared)) {
      setPagination((prev) =>
        prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
      );
      setPageIndex(0);
    }

    previousDebouncedSearchRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm, setPageIndex]);

  const { columnVisibility, setColumnVisibility } = useColumnVisibility({
    orgId: curUserOrg?.id,
    defaultVisibility: DEFAULT_COLUMN_VISIBILITY,
    configKey: 'drive_table_column_config',
  });

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({
        title: '',
        description: 'Link copied to clipboard',
        status: 'success',
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const {
    data: filteredData,
    meta,
    isError,
    isLoading,
    isRefetching,
    isSuccess,
    refetch,
  } = usePrivateListQuery(
    curUserOrg,
    orderBy,
    sortOrder,
    pagination.pageIndex,
    pagination.pageSize,
    location.search,
    isQueryEnabled,
    debouncedSearchTerm
  );

  // When local index is ready, sorting and pagination are handled in-memory.
  const localSortedResults = useMemo(() => {
    if (!isLocalDataMode) return null;

    const source = searchResults ?? allRecords;
    const sorted = [...source];
    const activeSort = sorting[0];

    if (activeSort?.id) {
      sorted.sort((a, b) =>
        compareDriveValues(a, b, activeSort.id, activeSort.desc)
      );
    }

    return sorted;
  }, [isLocalDataMode, searchResults, allRecords, sorting]);

  const clientPageData = useMemo(() => {
    if (!isLocalDataMode || !localSortedResults) return null;
    const start = pagination.pageIndex * pagination.pageSize;
    return localSortedResults.slice(start, start + pagination.pageSize);
  }, [
    isLocalDataMode,
    localSortedResults,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  const displayData = isLocalDataMode ? clientPageData ?? [] : filteredData;
  const displayMeta =
    isLocalDataMode && localSortedResults
      ? { totalCount: localSortedResults.length }
      : meta;

  const hasCurrentOrgBackendData =
    !!curUserOrg?.id &&
    isSuccess &&
    (filteredData.length === 0 ||
      filteredData.every((drive) => drive.organization_id === curUserOrg.id));

  useEffect(() => {
    const currentOrgId = curUserOrg?.id ?? null;
    const previousOrgId = previousOrgIdRef.current;

    if (
      previousOrgId !== null &&
      currentOrgId !== null &&
      previousOrgId !== currentOrgId
    ) {
      setOrgSwitchPending(true);
    }

    previousOrgIdRef.current = currentOrgId;
  }, [curUserOrg?.id]);

  useEffect(() => {
    if (!orgSwitchPending) return;

    if (isLocalDataMode || hasCurrentOrgBackendData) {
      setOrgSwitchPending(false);
    }
  }, [orgSwitchPending, isLocalDataMode, hasCurrentOrgBackendData]);

  const hasBackendResponse = isSuccess || isError;
  const isTablePending =
    orgSwitchPending ||
    (!isLocalDataMode &&
      !!curUserOrg?.id &&
      !hasBackendResponse &&
      (isOrgChanging || !isHydrated || !isSearchRestored));

  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [selectedColKeys, setSelectedColKeys] = useState<string[]>([]);
  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  /* 
    Control for recording publicity configuration modal
  */
  const {
    isOpen: isPublicitySettingOpen,
    onOpen: onPublicitySettingOpen,
    onClose: onPublicitySettingClose,
  } = useDisclosure();

  const publicityRecordingRef = useRef<
    RecordingPublicityAccessConfig | undefined
  >(undefined);

  const onPublicitySettingCloseHandler = useCallback(async () => {
    publicityRecordingRef.current = undefined;
    if (localStorage.getItem('recordingAccessConfigChanged')) {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const firstKey = query.queryKey[0];
          return [
            'shared-from-list',
            'drive-list',
            'private-public-drive-list',
            'public-public-drive-list',
          ].includes(firstKey as string);
        },
      });

      // Keep list query and local search index in sync when publication UUID changes.
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: ['public-public-drive-list'],
        }),
        refetch(),
        refreshIndex(),
      ]);

      localStorage.removeItem('recordingAccessConfigChanged');
    }

    onPublicitySettingClose();
  }, [onPublicitySettingClose, queryClient, refetch, refreshIndex]);

  /* 
    We need the full category list for filtering, however, we are showing
    shortened, mapped list in the popover since we need to use the shortened selected list
    when pass to filter column popover.
  */
  const shortenedFilters = shortenFilterForPopover(filters);

  const handleSortingChange: MRT_TableOptions<ListDriveInfo>['onSortingChange'] =
    (updater) => {
      setSorting((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (s: MRT_SortingState) => MRT_SortingState)(prev)
            : updater;

        if (next.length) {
          const { id: accessorKey, desc } = next[0];
          if (!isHydrated) {
            return next;
          }

          if (
            accessorKey ===
              baseRecordingColumnMapping[BaseRecordingTableColumn.RECORDED] ||
            accessorKey ===
              baseRecordingColumnMapping[BaseRecordingTableColumn.UPLOADED]
          ) {
            setOrderBy(accessorKey, desc ? 'desc' : 'asc');
          }
        }

        return next;
      });
    };

  const customTableOptions: Partial<MRT_TableOptions<ListDriveInfo>> = {
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    renderRowActions: ({ row }) => (
      <RowActions
        row={row}
        copyCloudVizLink={copyCloudVizLink}
        copyCloudPublicLink={copyCloudPublicLink}
        openRecordingDownloadPopover={openRecordingDownloadPopover}
        handleDelete={handleDelete}
        reprocessRecording={reprocessRecording}
        configRecordingPublicity={configRecordingPublicity}
        updateRecordingInfo={updateRecordingInfo}
        reUploadExtrinsic={reUploadExtrinsic}
      />
    ),
    enableColumnPinning: false,
    enableHiding: true,
    enableTopToolbar: true,
    enableRowVirtualization: true,
    enableSortingRemoval: false,
    layoutMode: 'grid',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: 'ACTIONS',
        size: 140,
        minSize: 140,
        maxSize: 140,
      },
    },
    rowVirtualizerOptions: {
      overscan: 10,
      estimateSize: () => 55,
    },
    rowCount: displayMeta?.totalCount ?? 0,
    state: {
      isLoading: isLoading || isReprocessing || isTablePending,
      pagination,
      showLoadingOverlay: false,
      showAlertBanner: isError,
      showProgressBars:
        isLoading ||
        isRefetching ||
        isManualRefreshing ||
        isIndexRefreshInFlight ||
        isTablePending,
      showSkeletons:
        isLoading || isReprocessing || isManualRefreshing || isTablePending,
      sorting,
      columnVisibility,
    },
    ...createPaginationConfig(displayMeta, pagination),
  };

  useEffect(() => {
    if (!isHydrated) return;

    setPagination((prev) => {
      if (prev.pageIndex === pageIndex) {
        return prev;
      }

      return { ...prev, pageIndex };
    });
  }, [isHydrated, pageIndex]);

  useEffect(() => {
    if (!isHydrated) return;

    setSorting((prev) => {
      const current = prev[0];
      const nextDesc = sortOrder === 'desc';
      const recordedKey =
        baseRecordingColumnMapping[BaseRecordingTableColumn.RECORDED];
      const uploadedKey =
        baseRecordingColumnMapping[BaseRecordingTableColumn.UPLOADED];

      // Keep client-side sorts (all non-date columns) stable across pagination.
      // URL sort params only represent backend date sorts.
      if (
        current?.id &&
        current.id !== recordedKey &&
        current.id !== uploadedKey
      ) {
        return prev;
      }

      if (current?.id === orderBy && current?.desc === nextDesc) {
        return prev;
      }

      return [{ id: orderBy, desc: nextDesc }];
    });
  }, [isHydrated, orderBy, sortOrder]);

  useEffect(() => {
    if (!curUserOrg?.id || isSearchRestored) return;

    if (location.search === '') {
      const storedSearch = localStorage.getItem(`search_${curUserOrg.id}`);
      if (storedSearch) {
        navigate({ search: storedSearch }, { replace: true });
      }
    }

    setSearchRestoredOrgId(curUserOrg.id);
  }, [curUserOrg?.id, isSearchRestored, location.search, navigate]);

  /* 
    we will update local storage when
    - data is successfully fetched (live or cached)
    - and default query is setup so no accidentally wipe useful saved query
    - it doesn't happen during switch organization
  */
  useEffect(() => {
    if (curUserOrg?.id && isHydrated && isSuccess && !isOrgChanging) {
      saveSearchToLocalStorage(location.search, curUserOrg.id);
    }
  }, [curUserOrg?.id, isHydrated, isSuccess, location.search, isOrgChanging]);

  /* 
    Whenever user switch organization. We need to clear the previous filters and invalidate query
  */
  useEffect(() => {
    if (curUserOrg?.id && isOrgChanging) {
      setSearchTerm('');
      setDebouncedSearchTerm('');
      previousDebouncedSearchRef.current = '';

      queryClient.invalidateQueries({ queryKey: ['drive-list'] });
      // get search query based on orgId
      const storedSearch = localStorage.getItem(`search_${curUserOrg.id}`);

      navigate({
        pathname: location.pathname,
        search: storedSearch || '',
      });
      sessionStorage.removeItem('orgChanged');
    }
  }, [curUserOrg?.id, location.pathname, navigate, queryClient, isOrgChanging]);

  useEffect(() => {
    if (curUserOrg?.id) {
      loadOrgFilterOptions(curUserOrg.id);
    }
  }, [curUserOrg?.id, loadOrgFilterOptions]);

  /* 
    not open the viz page unless it is viewable
  */
  function openVizPage(recording: ListDriveInfo) {
    if (isLoading) return;
    if (hasUserSelectionOnTable()) return;
    if (recording.status !== RecordingProcessStatus.Processing) {
      const url = `/organization/${recording.organization_id}/drive/${recording.id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  function copyCloudVizLink(recording: ListDriveInfo) {
    const url = `/organization/${recording.organization_id}/drive/${recording.id}`;
    copyToClipboard(`${window.location.origin}${url}`);
  }

  function copyCloudPublicLink(recording: ListDriveInfo) {
    if (recording.public_url) copyToClipboard(recording.public_url);
  }

  async function reprocessRecording(recording: ListDriveInfo) {
    try {
      if (!curUserOrg?.id) return;
      setIsReprocessing(true);
      await reprocessDrive({ orgId: curUserOrg.id, driveId: recording.id });

      updateIndexedDrive(Number(recording.id), (drive) => ({
        ...drive,
        status: RecordingProcessStatus.Processing,
      }));

      const queryKey = [
        'drive-list',
        curUserOrg.id,
        orderBy,
        sortOrder,
        pagination.pageIndex,
        pagination.pageSize,
        location.search,
      ];

      queryClient.setQueryData<{ data: ListDriveInfo[] }>(
        queryKey,
        (oldData) => {
          if (!oldData) return undefined;

          return {
            ...oldData,
            data: oldData.data.map((item) =>
              item.id === recording.id
                ? { ...item, status: RecordingProcessStatus.Processing }
                : item
            ),
          };
        }
      );

      queryClient.invalidateQueries({ queryKey: ['drive-list'] });
      showToast({
        title: '',
        description: `Reprocessing recording ${recording.id}`,
        status: 'success',
      });
    } catch (error) {
      showToast({
        title: 'Failed to reprocess recording',
        description: `${error}`,
        status: 'error',
      });
    } finally {
      setIsReprocessing(false);
    }
  }

  function reUploadExtrinsic(recording: ListDriveInfo) {
    extrinsicRecordingRef.current = recording;
    onExtrinsicOpen();
  }

  const [isUpdatingRecording, setIsUpdatingRecording] = useState(false);

  function updateRecordingInfo(recording: ListDriveInfo) {
    recordingInfoRef.current = recording;
    onRecordingInfoModalOpen();
  }
  async function submitRecordingInfoChange(recording: ListDriveInfo) {
    try {
      if (!curUserOrg?.id || !recording.recording_title) return;
      setIsUpdatingRecording(true);
      await updateRecordingTitle({
        orgId: curUserOrg.id,
        driveId: recording.id as number,
        title: recording.recording_title,
      });

      updateIndexedRecordingTitle(
        recording.id as number,
        recording.recording_title
      );

      // instead of refetch the whole list, we update the specific item in the cache
      const queryKey = [
        'drive-list',
        curUserOrg.id,
        orderBy,
        sortOrder,
        pagination.pageIndex,
        pagination.pageSize,
        location.search,
      ];

      queryClient.setQueryData<{ data: ListDriveInfo[] }>(
        queryKey,
        (oldData) => {
          if (!oldData) return undefined;

          return {
            ...oldData,
            data: oldData.data.map((item: ListDriveInfo) =>
              item.id === recording.id
                ? { ...item, recording_title: recording.recording_title }
                : item
            ),
          };
        }
      );

      showToast({
        title: '',
        description: `Recording title has been updated`,
        status: 'success',
      });

      onRecordingInfoModalClose();
      recordingInfoRef.current = undefined;
    } catch (error) {
      showToast({
        title: 'Failed to update recording',
        description: `${error}`,
        status: 'error',
      });
    } finally {
      setIsUpdatingRecording(false);
    }
  }

  function configRecordingPublicity(recording: ListDriveInfo) {
    publicityRecordingRef.current = {
      organization_id: recording.organization_id,
      id: Number(recording.id),
      publication_uuid: recording.publication_uuid,
      data_category: recording.data_category,
      sensor_info: recording.sensor_info,
      start_time: recording.start_time,
      recording_title: recording.recording_title,
    };

    onPublicitySettingOpen();
  }

  function handleDelete(drive: ListDriveInfo) {
    const onConfirm = async () => {
      if (!curUserOrg?.id) return;
      try {
        await deleteDrive({ orgId: curUserOrg.id, driveId: drive.id });
        // reload the filter options in case the delete record remove category and tags
        // existed in previous one
        await loadOrgFilterOptions(curUserOrg.id);

        removeIndexedRecording(Number(drive.id));

        const queryKey = [
          'drive-list',
          curUserOrg.id,
          orderBy,
          sortOrder,
          pagination.pageIndex,
          pagination.pageSize,
          location.search,
        ];

        queryClient.setQueryData<{ data: ListDriveInfo[] }>(
          queryKey,
          (oldData) => {
            if (!oldData) return undefined;

            return {
              ...oldData,
              data: oldData.data.filter((item) => item.id !== drive.id),
            };
          }
        );

        queryClient.invalidateQueries({ queryKey: ['drive-list'] });

        // when an ouster published drive is deleted. invalidate sample data query
        if (
          drive.publication_access_level !== AccessLevel.NONE &&
          curUserOrg.id === 1
        ) {
          queryClient.invalidateQueries({
            queryKey: ['public-public-drive-list'],
          });
          queryClient.invalidateQueries({
            queryKey: ['private-public-drive-list'],
          });

          loadNewContentStatus({ organizationId: curUserOrg.id });
        }

        // from list page, we won't be able to know whether the drive is shared or not
        // so we need to invalidate the shared list query any way so that the deleted drive
        // won't be shown in the shared list
        queryClient.invalidateQueries({ queryKey: ['shared-with-list'] });
        queryClient.invalidateQueries({ queryKey: ['shared-from-list'] });
      } catch (error) {
        showToast({
          title: 'Failed to delete recording',
          description: `${error}`,
          status: 'error',
        });
      }
    };

    if (curUserOrg?.id) {
      const bullets = [
        'This recording will be permanently deleted and cannot be recovered.',
      ];
      if (drive.public_url) {
        bullets.push(
          'This recording is published and will be removed from public listings.'
        );
      }
      openConfirmDialog({
        title: 'Delete recording',
        body: `Are you sure you want to delete recording?`,
        bullets,
        onConfirm,
        isDestructive: true,
        confirmLabel: 'Delete recording',
        cancelLabel: 'Cancel',
      });
    }
  }
  /* 
    Click on download button will triggered a popover with all downloadable links
  */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [downloadRecordingId, setDownloadRecordingId] = useState('');
  const downloadRecordingTitleRef = useRef<string | undefined>(undefined);
  const downloadRecordingIsSlamProcessingRef = useRef(false);

  function openRecordingDownloadPopover(
    recording: ListDriveInfo,
    event?: React.MouseEvent<HTMLButtonElement>
  ) {
    if (!event) return;
    setAnchorEl(event.currentTarget);
    setDownloadRecordingId(recording.id.toString());
    downloadRecordingTitleRef.current = recording.recording_title;
    downloadRecordingIsSlamProcessingRef.current =
      recording.slam_generation_status === RecordingStatus.Running;
  }

  const closeDownloadMenu = useCallback(() => {
    setOutputFileInfo([]);
    setSourceFileInfo([]);
    setAnchorEl(null);
  }, [setOutputFileInfo, setSourceFileInfo]);

  function clearAllFilters() {
    if (curUserOrg?.id) {
      localStorage.removeItem(`search_${curUserOrg.id}`);
    }

    navigate({
      pathname: location.pathname,
      search: '', // Clear search parameters
    });
  }

  const handleManualRefresh = useCallback(async () => {
    setIsManualRefreshing(true);
    try {
      if (isLocalDataMode) {
        // Keep index refresh async so backend data can render immediately.
        refreshIndex().catch((error) => {
          console.error('Failed to refresh search index: ', error);
        });
      }

      await refetch();
    } finally {
      setIsManualRefreshing(false);
    }
  }, [isLocalDataMode, refreshIndex, refetch]);

  /* 
    we need to refresh the search index and query data after uploading a new recording. The event is sent from AppRoutes.tsx
  */
  useEffect(() => {
    const handleExternalRefresh = () => {
      handleManualRefresh().catch((error) => {
        console.error('Failed to refresh recordings list: ', error);
      });
    };

    window.addEventListener(
      'private-recordings-manual-refresh',
      handleExternalRefresh
    );

    return () => {
      window.removeEventListener(
        'private-recordings-manual-refresh',
        handleExternalRefresh
      );
    };
  }, [handleManualRefresh]);

  // Optimistically insert newly uploaded drives into the local search index
  // so the user sees their upload immediately without waiting for the
  // materialized view to refresh on the backend.
  useEffect(() => {
    const handleDriveCreated = (e: Event) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const detail = (e as CustomEvent).detail as {
        driveId: number;
        orgId: number;
        tags: string[];
        dataCategory: string | null;
        dataFile: string;
        uploadedBy: string | null;
        processedAt: string;
        sdk_sensor_channel_type: string | null;
        sensorInfo: Array<{
          sdk_prod_sn: string | null;
          sdk_prod_line: string | null;
          sdk_prod_type: string | null;
          sdk_prod_generation: string | null;
          sdk_prod_hw_rev: string | null;
          sdk_prod_beam_spacing: string | null;
          sdk_prod_resolution: string | null;
          sdk_fw_version_core: string | null;
          sdk_lidar_mode: string | null;
          sdk_fw_branch: string | null;
          sdk_fw_build_rev: string | null;
          sdk_fw_image_rev: string | null;
          sdk_sensor_channel_type: string | null;
        }>;
      };

      // Only insert if the upload belongs to the current org
      if (detail.orgId !== curUserOrg?.id) return;

      const record: SearchIndexRecord = {
        id: detail.driveId,
        recording_title: null,
        source_prefix: null,
        upload_status: 'UPLOADED',
        processed_at: detail.processedAt,
        start_time: null,
        stop_time: null,
        osf_generation_status: null,
        visualization_status: 'UNKNOWN',
        slam_generation_status: null,
        data_type: 'SDK',
        data_category: detail.dataCategory,
        uploaded_by: detail.uploadedBy,
        uploaded_by_id: null,
        organization_id: detail.orgId,
        publication_id: null,
        publication_uuid: null,
        tags: detail.tags,
        sdk_sensor_channel_type: detail.sdk_sensor_channel_type ?? null,
        sensor_info: detail.sensorInfo,
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      addIndexedRecording(record);

      // Also invalidate React Query so it refetches from the live table
      queryClient.invalidateQueries({ queryKey: ['drive-list'] });

      // Debounced delayed refresh: reset the timer on each upload so
      // multiple uploads consolidate into a single reconciliation.
      if (reconcileTimerRef.current !== null) {
        window.clearTimeout(reconcileTimerRef.current);
      }
      reconcileTimerRef.current = window.setTimeout(() => {
        reconcileTimerRef.current = null;
        handleManualRefresh().catch((error) => {
          console.error('Failed to reconcile after upload:', error);
        });
      }, 5 * 60 * 1000);
    };

    window.addEventListener('upload-drive-created', handleDriveCreated);
    return () => {
      window.removeEventListener('upload-drive-created', handleDriveCreated);
      if (reconcileTimerRef.current !== null) {
        window.clearTimeout(reconcileTimerRef.current);
      }
    };
  }, [curUserOrg?.id, addIndexedRecording, queryClient, handleManualRefresh]);

  const totalSelectedFilters = Object.values(filters).reduce(
    (total, filterValues) => total + filterValues.length,
    0
  );

  if (!curUserOrg) return null;

  return (
    <>
      <Box
        p="25px 36px 0px"
        h="100%"
        overflow="hidden"
        position="relative"
        display="grid"
        gridTemplateRows="auto auto 1fr"
        gap="1rem"
        data-testid="private-recordings-root"
      >
        <Box display="grid" rowGap="1rem">
          <Box
            display="flex"
            placeContent="space-between"
            alignItems="center"
            mb={6}
          >
            <H2 fontWeight="medium" fontSize={30}>
              Recordings
            </H2>
            <Button
              variant="ghost"
              colorScheme="gray"
              gap={1}
              onClick={() => {
                handleManualRefresh().catch((error) => {
                  console.error('Failed to refresh recordings list: ', error);
                });
              }}
              isLoading={isManualRefreshing}
              data-testid="private-recordings-refresh"
            >
              <MdRefresh size={18} /> Refresh List
            </Button>
          </Box>

          {/* filters and search controls */}
          <Box
            display="flex"
            gap={2}
            alignItems="center"
            flexWrap="wrap"
            mr="50px"
            zIndex={2}
            data-testid="private-recordings-filters"
          >
            <InputGroup
              maxW="40ch"
              data-testid="private-recordings-search-group"
            >
              <InputLeftElement pointerEvents="none">
                <MdSearch size={18} />
              </InputLeftElement>
              <TextInput
                pl="2.5rem"
                pr="3.5rem"
                placeholder="Search recordings, sensors, tags..."
                type="search"
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                data-testid="private-recordings-search-input"
              />
              <InputRightElement>
                <IconButton
                  onClick={() => setSearchTerm('')}
                  data-testid="private-recordings-search-clear"
                >
                  <MdClear size={16} />
                </IconButton>
              </InputRightElement>
            </InputGroup>

            {FILTER_GROUPS.map((groupedFilter) => (
              <FilterDropDown
                title={groupedFilter.title}
                filterKey={groupedFilter.filterKeys}
                filterOptions={filterOptions}
                setGroupFilters={setGroupFilters}
                selectedFilters={filters}
                key={groupedFilter.title}
                data-testid={`private-recordings-filter-${groupedFilter.title
                  .toLowerCase()
                  .replace(/\s/g, '-')}`}
              />
            ))}

            {/* 
            Clear all filters button
          */}
            {totalSelectedFilters > 0 && (
              <Button
                variant="ghost"
                colorScheme="gray"
                gap={1}
                onClick={() => clearAllFilters()}
                fontSize="12px"
                data-testid="private-recordings-clear-filters"
              >
                <MdClear size={18} /> Clear Filters
              </Button>
            )}
          </Box>
        </Box>
        <Box
          overflow="hidden"
          position="relative"
          bg="rgb(29, 29, 29)"
          mt="-56px"
          zIndex={1}
          ref={tableContainerRef}
          data-testid="private-recordings-table-container"
        >
          <OrganizationRecording
            key={`${curUserOrg.id}`}
            filters={filters || {}}
            maxHeight="calc(100vh - 320px)"
            filteredData={displayData}
            setFilterAnchorEl={setFilterAnchorEl}
            setSelectedColKeys={setSelectedColKeys}
            openVizPage={openVizPage}
            customTableOptions={customTableOptions}
            isOuster={isOusterUser}
          />
        </Box>
      </Box>
      {anchorEl && (
        <RecordingDownloadPopover
          anchorEl={anchorEl}
          closeDownloadMenu={closeDownloadMenu}
          downloadRecordingId={downloadRecordingId}
          orgId={curUserOrg.id}
          recordingTitle={downloadRecordingTitleRef.current}
          isSlamProcessing={downloadRecordingIsSlamProcessingRef.current}
          data-testid="private-recordings-download-popover"
        />
      )}

      {filterAnchorEl && (
        <ColumnFilterPopover
          anchorEl={filterAnchorEl}
          onClose={handleCloseFilterPopover}
          filterKey={selectedColKeys}
          filterOptions={filterOptions}
          selectedFilters={shortenedFilters}
          setGroupFilters={setGroupFilters}
          data-testid="private-recordings-column-filter-popover"
        />
      )}

      {extrinsicRecordingRef.current && (
        <ModalContainer
          isOpen={isExtrinsicOpen}
          onClose={onExtrinsicClose}
          header="Re-upload extrinsic file"
          footer={null}
          content={
            <ReUploadExtrinsic
              orgId={curUserOrg.id}
              recording={extrinsicRecordingRef.current}
              reprocessRecording={reprocessRecording}
              onClose={onExtrinsicClose}
            />
          }
          data-testid="private-recordings-extrinsic-modal"
        />
      )}

      {recordingInfoRef.current && (
        <EditRecording
          recording={recordingInfoRef.current}
          onClose={onRecordingInfoModalClose}
          onSubmit={submitRecordingInfoChange}
          isLoading={isUpdatingRecording}
          isRecordingInfoModalOpen={isRecordingInfoModalOpen}
          heading="Edit Recording"
          data-testid="private-recordings-edit-recording-modal"
        />
      )}

      {publicityRecordingRef.current && (
        <ModalContainer
          isOpen={isPublicitySettingOpen}
          onClose={onPublicitySettingCloseHandler}
          header="Recording Access Configuration"
          footer={null}
          content={
            <RecordingAccessConfig
              recording={publicityRecordingRef.current}
              onClose={onPublicitySettingCloseHandler}
            />
          }
          data-testid="private-recordings-access-config-modal"
        />
      )}
    </>
  );
}
